import { useEffect, useState } from "react";
import { Modal } from "../common/Modal";
import { Button } from "../common/Button";
import { Field } from "../common/Field";
import { equipmentApi } from "../../services/equipmentApi";
import { useToast } from "../../context/ToastContext";

const MUTED = { color: "var(--color-text-muted)" };

/**
 * Two names for one piece of kit -- or five (#299, #409).
 *
 * Raised 27 Aug: people with long equipment lists create duplicates.
 * Case folding already stops "frame" and "Frame" coexisting -- the ones
 * that happen are "Frame" and "Frames", "Deep" and "Deep Box", "Hive
 * Tool" and "Hivetool".
 *
 * ── WHY IT SHOWS THE ARITHMETIC BEFORE IT COMMITS ──
 *
 * Equipment quantity is DERIVED from the movement ledger (V074), so
 * merging is not something the user can check afterwards by reading a
 * number off a row -- the row is gone. The combined figure has to be on
 * screen BEFORE the button, because that is the only moment it can be
 * disagreed with.
 *
 * All three figures, not just the total. "Out on hives" is the one that
 * surprises people: forty frames owned with eighteen nailed into hives
 * is a different situation from forty sitting in a shed, and somebody
 * merging two rows is often doing it precisely because the numbers look
 * wrong.
 *
 * ── THE DIRECTION IS EXPLICIT, AND IT IS NOT SYMMETRIC ──
 *
 * One row survives with its name, threshold, supplier and cost; the
 * others are folded into it. A dialog that said "merge these" and picked
 * for you would be choosing which name the account lives with.
 *
 * NOTHING IS DELETED -- each losing row records where its stock went --
 * but nothing in the app un-merges either, so this asks plainly.
 *
 * ── SEVERAL AT ONCE, BECAUSE THAT IS HOW THEY ARRIVE (#409) ──
 *
 *     "if it's possible to allow multi-select for the merging. My
 *      AtlarJY account has a bunch of entries for each and I can only
 *      merge them 1 at a time currently."
 *
 * Duplicates come in clusters, and the one-at-a-time version made the
 * user choose the survivor afresh for each stray spelling -- four
 * chances to pick a different one, with no screen anywhere that would
 * show afterwards that they had. So the survivor is chosen ONCE and the
 * rest are ticked.
 *
 * CHECKBOXES RATHER THAN A MULTIPLE SELECT. A <select multiple> needs
 * ctrl-click to pick a second item and loses the lot on a stray plain
 * click, which is a bad trade on a control whose whole job is picking
 * several -- and a silently emptied selection here reads as the dialog
 * having forgotten what you told it.
 */
export function MergeEquipmentDialog({ open, inventory, stock, onClose, onMerged }) {
  const { notify } = useToast();
  const [keepId, setKeepId] = useState("");
  const [mergeIds, setMergeIds] = useState([]);
  const [busy, setBusy] = useState(false);

  // Cleared on open, not on close: leaving the last selection in place
  // would offer a merge nobody asked for to whoever opens this next --
  // and it matters more now that the selection can be five rows deep.
  useEffect(() => {
    if (open) {
      setKeepId("");
      setMergeIds([]);
      setBusy(false);
    }
  }, [open]);

  const figures = (id) => stock.find((s) => String(s.equipment_id) === String(id));
  const keep = figures(keepId);

  // The survivor can never also be a target. Dropping it here rather
  // than only hiding its checkbox means changing the survivor to
  // something already ticked cannot leave a merge-into-itself queued.
  const losers = mergeIds
    .filter((id) => String(id) !== String(keepId))
    .map(figures)
    .filter(Boolean);

  const ready = Boolean(keep) && losers.length > 0;

  const combined = ready && losers.reduce((sum, l) => ({
    owned: sum.owned + l.owned,
    needs_repair: sum.needs_repair + l.needs_repair,
    out_on_hives: sum.out_on_hives + l.out_on_hives,
    available: sum.available + l.available,
  }), {
    owned: keep.owned,
    needs_repair: keep.needs_repair,
    out_on_hives: keep.out_on_hives,
    available: keep.available,
  });

  function toggle(id) {
    setMergeIds((prev) => (prev.some((p) => String(p) === String(id))
      ? prev.filter((p) => String(p) !== String(id))
      : [...prev, id]));
  }

  async function submit() {
    setBusy(true);
    try {
      const r = await equipmentApi.merge(
        Number(keepId), losers.map((l) => Number(l.equipment_id)));

      /*
       * PER ITEM, because the server answers per item. The merge is a
       * loop rather than a transaction -- execute_command commits per
       * statement -- so "some of them worked" is a real outcome, and a
       * flat success toast over it would be a lie the user only finds
       * out about by counting rows.
       */
      const done = r.merged.length;
      const refused = r.refused || [];
      if (refused.length === 0) {
        notify(
          done === 1
            ? `"${r.merged[0].item_type}" folded into "${r.kept.item_type}". Nothing was deleted.`
            : `${done} items folded into "${r.kept.item_type}". Nothing was deleted.`,
          "success",
        );
      } else {
        notify(
          `${done} of ${done + refused.length} folded into "${r.kept.item_type}". `
          + `${refused.map((f) => `"${f.item_type}"`).join(", ")} could not be merged.`,
          "warning",
        );
      }
      onMerged();
    } catch (err) {
      notify(err.apiMessage || "Could not merge those items", "danger");
      setBusy(false);
    }
  }

  const candidates = (inventory || [])
    .filter((i) => String(i.equipment_id) !== String(keepId));

  const row = (label, f, key) => (
    <tr key={key}>
      <th scope="row" style={{ textAlign: "left", fontWeight: 400 }}>{label}</th>
      <td>{f.owned}</td><td>{f.needs_repair}</td>
      <td>{f.out_on_hives}</td><td>{f.available}</td>
    </tr>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Merge duplicate equipment"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !ready}>
            {busy
              ? "Merging…"
              : losers.length > 1 ? `Merge ${losers.length} items` : "Merge them"}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: "var(--space-3)" }}>
        <p style={{ margin: 0, ...MUTED }}>
          One item keeps its name and settings. Everything recorded against the
          others — purchases, losses, repairs, what is out on hives — moves
          across. Nothing is deleted, but this cannot be undone from here.
        </p>

        <Field label="Keep this one" hint="Its name, reorder level and supplier stay as they are.">
          {(id, aria) => (
            <select id={id} {...aria} className="touch-target" value={keepId}
              onChange={(e) => setKeepId(e.target.value)}>
              <option value="">Choose…</option>
              {(inventory || []).map((i) => (
                <option key={i.equipment_id} value={i.equipment_id}>{i.item_type}</option>
              ))}
            </select>
          )}
        </Field>

        {/* Not a Field: it is a group of checkboxes rather than one
            control, so it needs a fieldset and a legend to be announced
            as the one question it is. */}
        <fieldset style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
          <legend style={{ padding: 0, fontWeight: 600 }}>Fold these into it</legend>
          <p style={{ margin: "0 0 var(--space-2)", fontSize: "var(--font-size-sm)", ...MUTED }}>
            Tick every spelling of the same thing. They stop appearing in your lists.
          </p>

          {keepId === "" ? (
            /* Asking which to fold in before there is anything to fold
               into invites ticking five rows and then discovering the
               one you meant to keep is among them. */
            <p style={{ margin: 0, ...MUTED }}>Choose the item to keep first.</p>
          ) : (
            <div style={{
              maxHeight: "14rem",
              overflowY: "auto",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-sm)",
              padding: "var(--space-2)",
            }}>
              {candidates.map((i) => (
                <label
                  key={i.equipment_id}
                  className="touch-target"
                  style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}
                >
                  <input
                    type="checkbox"
                    checked={mergeIds.some((p) => String(p) === String(i.equipment_id))}
                    onChange={() => toggle(i.equipment_id)}
                  />
                  <span>{i.item_type}</span>
                </label>
              ))}
            </div>
          )}
        </fieldset>

        {ready && (
          /* THE ARITHMETIC, BEFORE THE BUTTON. The quantities are derived
             from the ledger, so after the merge there is no second row to
             check this against -- here is the only moment it can be
             disagreed with. Every item ticked gets its own line: a single
             combined total would hide the one row whose numbers are the
             reason somebody opened this dialog. */
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left", ...MUTED, fontSize: "var(--font-size-sm)" }}>
                <th scope="col">&nbsp;</th>
                <th scope="col">Owned</th>
                <th scope="col">Needs repair</th>
                <th scope="col">Out on hives</th>
                <th scope="col">Available</th>
              </tr>
            </thead>
            <tbody>
              {row(keep.item_type, keep, "keep")}
              {losers.map((l) => row(l.item_type, l, l.equipment_id))}
              <tr style={{ borderTop: "2px solid var(--color-border)", fontWeight: 700 }}>
                <th scope="row" style={{ textAlign: "left" }}>
                  {keep.item_type} after merging
                </th>
                <td>{combined.owned}</td><td>{combined.needs_repair}</td>
                <td>{combined.out_on_hives}</td><td>{combined.available}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
}
